import { useEffect, useState } from "react";

/**
 * Landing page for Zerodha's post-login redirect.
 *
 * Kite Connect redirects here with `?request_token=XXX&action=login&status=success`.
 * This page reads request_token from the URL, posts it back to the opener
 * window via postMessage, then prompts the user to close the tab. The opener
 * (BrokersPage's "Generate access token" button) does the actual exchange
 * with api_secret + request_token via /broker-connections/zerodha/exchange-token.
 *
 * No authentication required — the request_token alone isn't useful without
 * api_secret, which never leaves the opener window's memory.
 */
export default function ZerodhaCallbackPage() {
  const [status, setStatus] = useState<string>("Reading request_token…");

  useEffect(() => {
    const url = new URL(window.location.href);
    const requestToken = url.searchParams.get("request_token");
    const errorMsg = url.searchParams.get("error_description");
    if (errorMsg) {
      setStatus(`Kite login error: ${errorMsg}`);
      window.opener?.postMessage(
        { type: "zerodha-request-token-error", error: errorMsg },
        window.location.origin,
      );
      return;
    }
    if (!requestToken) {
      setStatus("No request_token in URL — did Kite redirect here directly?");
      return;
    }
    if (!window.opener) {
      setStatus(
        "Opened directly (no parent window). Copy this request_token manually:",
      );
      return;
    }
    window.opener.postMessage(
      { type: "zerodha-request-token", request_token: requestToken },
      window.location.origin,
    );
    setStatus("Token sent back to the broker form. You can close this tab.");
  }, []);

  const url = new URL(window.location.href);
  const requestToken = url.searchParams.get("request_token");

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-2">
      <div className="max-w-lg bg-surface rounded-lg border border-border shadow-sm p-8">
        <h1 className="text-lg font-semibold">Zerodha login complete</h1>
        <p className="mt-3 text-sm text-fg-subtle">{status}</p>
        {!window.opener && requestToken && (
          <pre className="mt-3 bg-surface-2 rounded px-3 py-2 text-xs font-mono break-all">
            {requestToken}
          </pre>
        )}
        <button
          type="button"
          onClick={() => window.close()}
          className="mt-5 text-sm text-up-700 dark:text-up-400 hover:underline"
        >
          Close window
        </button>
      </div>
    </div>
  );
}
